// Phase 6A · Reusable social publish panel (propose → preview → confirm).
// Mountable from MarketingContent and from an approved AI Studio draft row.
// Phase 6A closure addendum: now accepts an authoritative `pages` list
// sourced from GET /api/oauth/meta/status so the UI can select the exact
// connected Meta target. Legacy single-page prop (`pageId`) stays
// supported for AI-Studio-approved draft publish rows.
import { useEffect, useMemo, useState, useRef } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { proposePublish, confirmPublish } from "@/lib/marketingPublish";

export default function SocialPublishPanel({
  pageId = null,
  igAccountId = null,
  pages = null,                 // null → legacy single-page mode
  defaultMessage = "",
  defaultImageUrl = "",
  sourceEntityType = "manual",
  sourceEntityId = null,
  triggerLabel = "Publish",
  triggerDisabled = false,
  triggerTestId = "social-publish-open-btn",
  scheduleRequired = false,
  triggerVariant = "default",
  onPublished,
}) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(defaultMessage);
  const [imageUrl, setImageUrl] = useState(defaultImageUrl);
  const [scheduledAt, setScheduledAt] = useState("");
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);
  const [outcomeUnknown, setOutcomeUnknown] = useState(false);
  const requestPending = useRef(false);
  const previewEpoch = useRef(0);
  const invalidatePreview = () => {
    previewEpoch.current += 1;
    setPending(null);
  };
  useEffect(() => () => { previewEpoch.current += 1; }, []);

  // Target selection — authoritative only when `pages` is provided.
  const multiMode = Array.isArray(pages);
  const singlePage = multiMode && pages.length === 1 ? pages[0] : null;
  const [selectedPageId, setSelectedPageId] = useState(
    pageId || (singlePage ? singlePage.page_id : "")
  );
  useEffect(() => {
    if (!multiMode) return;
    // Keep the local selection coherent when the authoritative list changes.
    if (pages.length === 0) { setSelectedPageId(""); return; }
    if (pages.length === 1) { setSelectedPageId(pages[0].page_id); return; }
    // Multi-page: do not auto-pick; require explicit user action.
    if (selectedPageId && !pages.some((p) => p.page_id === selectedPageId)) {
      setSelectedPageId("");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(pages || [])]);

  const effectivePageId = multiMode ? selectedPageId : pageId;
  const activePageRow = useMemo(() => {
    if (!multiMode) return null;
    return pages.find((p) => p.page_id === effectivePageId) || null;
  }, [multiMode, pages, effectivePageId]);
  const effectiveIg = multiMode
    ? (activePageRow?.ig_account_id || null)
    : igAccountId;

  // Target checkboxes.
  const [targetFb, setTargetFb] = useState(true);
  const [targetIg, setTargetIg] = useState(!!effectiveIg);
  useEffect(() => {
    // When the selected page changes, re-seat IG default and invalidate
    // any pending preview so the payload hash binding stays truthful.
    setTargetIg(!!effectiveIg);
    invalidatePreview();
  }, [effectivePageId, effectiveIg, sourceEntityType, sourceEntityId]);

  const prepare = async () => {
    if (requestPending.current || outcomeUnknown) return;
    if (!effectivePageId) return toast.error("Select a connected Meta Page first");
    if (!message.trim()) return toast.error("Message required");
    if (!targetFb && !targetIg) return toast.error("Select Facebook or Instagram first");
    const image = imageUrl.trim();
    if (targetIg && !image) return toast.error("Add an image link for this Instagram post.");
    if (image) {
      try {
        const url = new URL(image);
        if (url.protocol !== "https:" || url.username || url.password || !url.hostname) throw new Error();
      } catch {
        return toast.error("Use a valid HTTPS image link without login credentials.");
      }
    }
    if (scheduleRequired && !scheduledAt) return toast.error("Choose a future date and time before preparing this scheduled post.");
    let scheduledIso = null;
    if (scheduledAt) {
      const selectedTime = new Date(scheduledAt);
      if (!Number.isFinite(selectedTime.getTime()) || selectedTime.getTime() <= Date.now()) {
        return toast.error("Choose a valid future date and time");
      }
      scheduledIso = selectedTime.toISOString();
    }
    requestPending.current = true;
    setBusy(true);
    const epoch = previewEpoch.current;
    try {
      const resp = await proposePublish({
        page_id: effectivePageId,
        target_fb: targetFb,
        target_ig: targetIg,
        message, image_url: image || null,
        scheduled_at: scheduledIso,
        source_entity_type: sourceEntityType,
        source_entity_id: sourceEntityId,
      });
      if (epoch === previewEpoch.current) setPending({
        ...resp,
        accountLabel: activePageRow?.page_name || "Selected Facebook Page",
        instagramLabel: activePageRow?.ig_username || null,
      });
    } catch (e) {
      if (epoch === previewEpoch.current) toast.error("The preview could not be prepared. Please try again.");
    } finally {
      requestPending.current = false;
      setBusy(false);
    }
  };

  const confirm = async () => {
    if (!pending || requestPending.current || outcomeUnknown) return;
    const expiry = new Date(pending.expires_at).getTime();
    if (!Number.isFinite(expiry) || expiry <= Date.now()) {
      invalidatePreview();
      toast.info("This preview has expired. Prepare a new preview before confirming.");
      return;
    }
    requestPending.current = true;
    setBusy(true);
    try {
      const res = await confirmPublish(
        pending.pending_id, pending.confirmation_token
      );
      if (res?.ok) {
        toast.success(res.mode === "scheduled" ? "Scheduled" : "Published");
        onPublished?.(res);
        setOpen(false); invalidatePreview();
        setMessage(defaultMessage); setImageUrl(defaultImageUrl);
        setScheduledAt("");
      } else {
        setOutcomeUnknown(true);
        toast.error("Publication was not confirmed. Check the account and scheduled posts before trying again.");
      }
    } catch (e) {
      setOutcomeUnknown(true);
      toast.error("The result is uncertain. The post may already be published or scheduled. Check its status before trying again.");
    } finally { requestPending.current = false; setBusy(false); }
  };

  const noPages = multiMode && pages.length === 0;
  const prepareDisabled = multiMode
    ? (noPages || !effectivePageId)
    : !pageId;

  return (
    <>
      <Button
        data-testid={triggerTestId}
        variant={triggerVariant}
        disabled={triggerDisabled}
        onClick={() => {
          if (requestPending.current || triggerDisabled) return;
          if (outcomeUnknown) { setOpen(true); return; }
          invalidatePreview();
          setMessage(defaultMessage);
          setImageUrl(defaultImageUrl);
          setOpen(true);
        }}
      >
        {triggerLabel}
      </Button>
      <Dialog open={open} onOpenChange={(v) => { if (requestPending.current) return; setOpen(v); if (!v && !outcomeUnknown) invalidatePreview(); }}>
        <DialogContent data-testid="social-publish-panel" className="max-w-xl">
          <DialogHeader>
            <DialogTitle>{scheduleRequired ? "Schedule a social post" : "Social publish"}</DialogTitle>
            <DialogDescription>
              AI drafts, you authorise. Nothing is posted until you confirm the exact payload below.
            </DialogDescription>
          </DialogHeader>
          {!pending ? (
            <div className="space-y-3">
              {multiMode && (
                noPages ? (
                  <div
                    data-testid="social-publish-no-pages"
                    className="text-sm bg-amber-50 border border-amber-200 text-amber-800 rounded-md p-2"
                  >
                    No connected Meta Page. Connect Facebook / Instagram
                    from Marketing → Connected accounts, then try again.
                  </div>
                ) : (
                  <div>
                    <Label>Connected Meta target</Label>
                    <select
                      data-testid="social-publish-page-select"
                      className="w-full border border-neutral-200 rounded-md px-2 py-1.5 text-sm bg-white"
                      value={effectivePageId}
                      onChange={(e) => {
                        setSelectedPageId(e.target.value);
                        invalidatePreview();
                      }}
                    >
                      {pages.length > 1 && (
                        <option value="">— Select a page —</option>
                      )}
                      {pages.map((p) => (
                        <option
                          key={p.page_id}
                          value={p.page_id}
                          data-testid={`social-publish-page-option-${p.page_id}`}
                        >
                          {p.page_name || p.page_id}
                          {p.ig_account_id ? "  ·  IG linked" : ""}
                        </option>
                      ))}
                    </select>
                    {activePageRow && (
                      <div
                        data-testid="social-publish-active-target"
                        className="mt-1 text-[11px] text-neutral-500"
                      >
                        Target page_id: <span className="font-mono">{activePageRow.page_id}</span>
                        {activePageRow.ig_account_id ? (
                          <>  ·  ig_account_id:
                            <span className="font-mono"> {activePageRow.ig_account_id}</span>
                          </>
                        ) : null}
                      </div>
                    )}
                  </div>
                )
              )}
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid="social-target-fb"
                    checked={targetFb}
                    onChange={(e) => { setTargetFb(e.target.checked); invalidatePreview(); }}
                  />
                  Facebook Page
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    data-testid="social-target-ig"
                    checked={targetIg}
                    disabled={!effectiveIg}
                    onChange={(e) => { setTargetIg(e.target.checked); invalidatePreview(); }}
                  />
                  Instagram {effectiveIg ? "" : "(not connected)"}
                </label>
              </div>
              <div>
                <Label>Message</Label>
                <Textarea
                  data-testid="social-publish-message"
                  rows={6}
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); invalidatePreview(); }}
                />
              </div>
              <div>
                <Label>Image URL (optional)</Label>
                <Input
                  data-testid="social-publish-image-url"
                  value={imageUrl}
                  onChange={(e) => { setImageUrl(e.target.value); invalidatePreview(); }}
                />
              </div>
              <div>
                <Label htmlFor={scheduleRequired ? "social-schedule-required" : "social-schedule-optional"}>
                  Schedule for (your local time{scheduleRequired ? ", required" : ", optional"})
                </Label>
                <Input
                  id={scheduleRequired ? "social-schedule-required" : "social-schedule-optional"}
                  autoFocus={scheduleRequired}
                  required={scheduleRequired}
                  data-testid="social-publish-scheduled-at"
                  type="datetime-local"
                  value={scheduledAt}
                  onChange={(e) => {
                    setScheduledAt(e.target.value);
                    invalidatePreview();
                  }}
                />
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <h3 className="font-semibold text-sm">Review your post</h3>
              <dl className="space-y-2 text-sm">
                <div>
                  <dt className="font-medium">Account</dt>
                  <dd>{pending.accountLabel}</dd>
                  <dd className="text-xs text-neutral-500">Facebook Page ID: {pending.preview?.page_id}</dd>
                  {pending.preview?.platform?.split("+").includes("meta_ig") && (
                    <dd>Instagram: {pending.instagramLabel ? `@${pending.instagramLabel}` : pending.preview?.ig_account_id}</dd>
                  )}
                </div>
                <div>
                  <dt className="font-medium">Publish to</dt>
                  <dd>{(pending.preview?.platform || "").split("+").map((platform) =>
                    platform === "meta_fb" ? "Facebook" : platform === "meta_ig" ? "Instagram" : "Unknown platform"
                  ).join(" and ")}</dd>
                </div>
                <div>
                  <dt className="font-medium">When</dt>
                  <dd>{pending.preview?.scheduled_at
                    ? new Date(pending.preview.scheduled_at).toLocaleString(undefined, { dateStyle: "full", timeStyle: "long" })
                    : "Immediately after you confirm"}</dd>
                </div>
              </dl>
              <div>
                <h4 className="font-medium text-sm mb-1">Message</h4>
                <div data-testid="social-publish-preview"
                  className="text-sm bg-neutral-50 border border-neutral-200 rounded-md p-3 whitespace-pre-wrap break-words">
                  {pending.preview?.message}
                </div>
              </div>
              <div className="text-sm">
                <h4 className="font-medium">Image</h4>
                {pending.preview?.image_url ? (
                  <p className="break-all">{pending.preview.image_url}</p>
                ) : <p>No image attached</p>}
              </div>
              <p className="text-xs text-neutral-500">
                Check the account, message and timing. Choose Back to make changes.
                Nothing is published or scheduled until you confirm.
              </p>
            </div>
          )}
          {outcomeUnknown && (
            <div role="alert" className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm">
              <p className="font-medium">Publication status needs checking</p>
              <p className="mt-1">Check the selected social account and your scheduled posts.
                An interrupted confirmation can still result in a published or scheduled post.
                Do not submit the same post again until you have checked.</p>
              <Button variant="outline" className="mt-3" onClick={() => {
                if (!window.confirm("Have you checked the social account and scheduled posts? Do not resubmit content that is already published or scheduled.")) return;
                setOutcomeUnknown(false);
                invalidatePreview();
                setOpen(false);
              }}>I have checked the status</Button>
            </div>
          )}
          <DialogFooter>
            {!pending ? (
              <Button
                data-testid="social-publish-prepare-btn"
                onClick={prepare}
                disabled={prepareDisabled || busy || outcomeUnknown}
              >
                {busy ? "Preparing…" : "Preview & prepare"}
              </Button>
            ) : (
              <>
                <Button
                  data-testid="social-publish-back-btn"
                  variant="outline" disabled={busy || outcomeUnknown}
                  onClick={invalidatePreview}
                >
                  Back
                </Button>
                <Button
                  data-testid="social-publish-confirm-btn"
                  disabled={busy || outcomeUnknown} onClick={confirm}
                >
                  {busy ? "Submitting…" : pending.preview?.scheduled_at ? "Confirm & schedule" : "Confirm & publish"}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
